export type QuestionOption = {
  emoji: string
  text: string
  tag: string
}

export type Question = {
  context: string
  question: string
  type: 'single' | 'multi'
  reinforcement: {
    highOptions: number[]
    high: string
    low: string
  } | null
  options: QuestionOption[]
}

export const quizData: Question[] = [
  {
    context: 'Primero, cuéntanos un poco sobre ti...',
    question: '¿Cuál es tu rol profesional actual?',
    type: 'single',
    reinforcement: null,
    options: [
      { emoji: '📋', text: 'Project Manager / Jefe de Proyecto', tag: 'PM' },
      { emoji: '💻', text: 'Profesional en Tecnología / IT', tag: 'TECH' },
      {
        emoji: '🤝',
        text: 'Consultor o Asesor de Negocios',
        tag: 'CONSULTANT',
      },
      {
        emoji: '🚀',
        text: 'Emprendedor / Dueño de negocio',
        tag: 'ENTREPRENEUR',
      },
      { emoji: '🎓', text: 'Estudiante o Recién Graduado', tag: 'STUDENT' },
      { emoji: '👥', text: 'Director de Equipo / Área', tag: 'DIRECTOR' },
    ],
  },
  {
    context: 'Excelente. Ahora dinos...',
    question: '¿En qué país estás ubicado actualmente?',
    type: 'single',
    reinforcement: null,
    options: [
      { emoji: '🇲🇽', text: 'México', tag: 'MEX' },
      { emoji: '🇨🇴', text: 'Colombia', tag: 'COL' },
      { emoji: '🇦🇷', text: 'Argentina', tag: 'ARG' },
      { emoji: '🇨🇱', text: 'Chile', tag: 'CHI' },
      { emoji: '🇵🇪', text: 'Perú', tag: 'PER' },
      { emoji: '🇪🇨', text: 'Ecuador', tag: 'ECU' },
      { emoji: '🌎', text: 'Otro país de Latinoamérica', tag: 'OTRO' },
    ],
  },
  {
    context: 'Perfecto. Una última pregunta para personalizar tu test...',
    question: '¿Cuántos años llevas trabajando en gestión de proyectos?',
    type: 'single',
    reinforcement: null,
    options: [
      {
        emoji: '⭐',
        text: 'Menos de 1 año — Estoy comenzando',
        tag: 'BEGINNER',
      },
      {
        emoji: '⭐⭐',
        text: '1 a 3 años — Tengo algo de experiencia',
        tag: 'JUNIOR',
      },
      {
        emoji: '⭐⭐⭐',
        text: '3 a 5 años — Tengo experiencia sólida',
        tag: 'INTERMEDIATE',
      },
      {
        emoji: '⭐⭐⭐⭐',
        text: '5 a 10 años — Soy un profesional experimentado',
        tag: 'EXPERIENCED',
      },
      {
        emoji: '⭐⭐⭐⭐⭐',
        text: 'Más de 10 años — Soy senior/experto',
        tag: 'ADVANCED',
      },
    ],
  },
  {
    context: 'Seamos honestos por un momento...',
    question:
      '¿Cuánto tiempo pierdes cada semana en tareas repetitivas que podrían automatizarse?\n(Reportes, actas, seguimiento, documentación...)',
    type: 'single',
    reinforcement: {
      highOptions: [2, 3],
      high: '⚠️ Eso equivale a 20-40 horas al mes. La IA puede recuperar hasta el 70% de ese tiempo.',
      low: '💡 Incluso 2 horas semanales automatizadas = 8 horas al mes para lo que realmente importa.',
    },
    options: [
      { emoji: '⏰', text: 'Menos de 2 horas — Es manejable', tag: 'LOW_PAIN' },
      {
        emoji: '⏰⏰',
        text: 'Entre 2 y 5 horas — Ya me molesta',
        tag: 'MED_PAIN',
      },
      {
        emoji: '⏰⏰⏰',
        text: 'Entre 5 y 10 horas — Es un problema real',
        tag: 'HIGH_PAIN',
      },
      {
        emoji: '⏰⏰⏰⏰',
        text: 'Más de 10 horas — Es insostenible',
        tag: 'EXTREME_PAIN',
      },
      {
        emoji: '🤷',
        text: 'No lo he medido pero sé que es mucho',
        tag: 'UNAWARE_PAIN',
      },
    ],
  },
  {
    context: 'Esto es importante. Sé honesto/a contigo...',
    question: '¿Cuál es tu MAYOR frustración al gestionar proyectos hoy?',
    type: 'single',
    reinforcement: null,
    options: [
      {
        emoji: '📄',
        text: 'La documentación me consume demasiado tiempo',
        tag: 'DOC_PAIN',
      },
      {
        emoji: '📊',
        text: 'Generar reportes y dashboards es lento y tedioso',
        tag: 'REPORT_PAIN',
      },
      {
        emoji: '👥',
        text: 'Coordinar al equipo y hacer seguimiento es un caos',
        tag: 'TEAM_PAIN',
      },
      {
        emoji: '🔄',
        text: 'Las reuniones y actas me quitan tiempo de trabajo real',
        tag: 'MEETING_PAIN',
      },
      {
        emoji: '🤖',
        text: 'No sé cómo integrar la IA en mi flujo de trabajo',
        tag: 'AI_PAIN',
      },
      {
        emoji: '📉',
        text: 'Me cuesta demostrar el valor e impacto de mis proyectos',
        tag: 'VALUE_PAIN',
      },
    ],
  },
  {
    context: 'Una pregunta que muchos evitan hacerse...',
    question:
      '¿Has sentido que tu perfil profesional podría quedar obsoleto por la IA en los próximos 2-3 años?',
    type: 'single',
    reinforcement: {
      highOptions: [0, 1, 2, 4],
      high: '💡 El 73% de las empresas en LATAM ya están integrando IA en sus procesos. Los profesionales que se certifiquen ahora tendrán ventaja por años.',
      low: '💡 El 73% de las empresas en LATAM ya están integrando IA en sus procesos. Los profesionales que se certifiquen ahora tendrán ventaja por años.',
    },
    options: [
      {
        emoji: '😰',
        text: 'Sí, me preocupa mucho — Lo pienso constantemente',
        tag: 'FEAR_HIGH',
      },
      {
        emoji: '😟',
        text: 'A veces lo pienso — Pero no sé qué hacer',
        tag: 'FEAR_MED',
      },
      {
        emoji: '😐',
        text: 'Un poco — Aunque prefiero no pensar en eso',
        tag: 'FEAR_LOW',
      },
      {
        emoji: '😎',
        text: 'No me preocupa — Estoy al día con la tecnología',
        tag: 'FEAR_NONE',
      },
      {
        emoji: '🤔',
        text: 'Nunca lo había pensado — Pero ahora que lo mencionas...',
        tag: 'FEAR_AWAKENED',
      },
    ],
  },
  {
    context: 'Vamos más a fondo en tu situación actual...',
    question:
      '¿Qué tan difícil te resulta mantener la documentación de tus proyectos organizada y actualizada?',
    type: 'single',
    reinforcement: null,
    options: [
      { emoji: '😤', text: 'Muy difícil — Es un caos total', tag: 'DOC_CHAOS' },
      {
        emoji: '😓',
        text: 'Bastante difícil — Lo mantengo pero me cuesta mucho esfuerzo',
        tag: 'DOC_HARD',
      },
      {
        emoji: '😐',
        text: 'Regular — Funciona pero podría ser mucho mejor',
        tag: 'DOC_OK',
      },
      {
        emoji: '🙂',
        text: 'Bastante bien — Tengo sistemas pero quiero optimizarlos con IA',
        tag: 'DOC_GOOD',
      },
      {
        emoji: '😊',
        text: 'Muy bien — Pero siempre busco mejorar',
        tag: 'DOC_OPTIMIZE',
      },
    ],
  },
  {
    context: 'Ahora evaluemos tu nivel actual con IA...',
    question:
      '¿Has usado alguna herramienta de Inteligencia Artificial en tu trabajo profesional?',
    type: 'single',
    reinforcement: {
      highOptions: [0],
      high: '💪 Perfecto. Ahora es momento de llevar tu uso de IA al siguiente nivel con metodología certificada.',
      low: '✅ No necesitas experiencia previa. El 68% de nuestros estudiantes empezaron desde cero y hoy lideran con IA en sus equipos.',
    },
    options: [
      {
        emoji: '🤖',
        text: 'Sí, uso IA regularmente en mi trabajo',
        tag: 'AI_POWER_USER',
      },
      {
        emoji: '🔍',
        text: 'He probado algunas pero no las uso de forma consistente',
        tag: 'AI_CASUAL',
      },
      {
        emoji: '👀',
        text: 'Las conozco pero no las he usado profesionalmente',
        tag: 'AI_AWARE',
      },
      {
        emoji: '❓',
        text: 'He oído hablar de ellas pero no sé por dónde empezar',
        tag: 'AI_CURIOUS',
      },
      {
        emoji: '🚫',
        text: 'Nunca he usado herramientas de IA profesionalmente',
        tag: 'AI_BEGINNER',
      },
    ],
  },
  {
    context: 'De estas herramientas de IA para gestión de proyectos...',
    question:
      '¿Cuáles conoces o has usado aunque sea una vez? (Selecciona todas las que apliquen)',
    type: 'multi',
    reinforcement: {
      highOptions: [],
      high: '🎯 Hemos identificado las herramientas específicas que más impacto tendrían en tu gestión. Aparecerá en tu plan.',
      low: '🎯 Hemos identificado las herramientas específicas que más impacto tendrían en tu gestión. Aparecerá en tu plan.',
    },
    options: [
      {
        emoji: '🤖',
        text: 'ChatGPT — Para generar texto e ideas',
        tag: 'TOOL_CHATGPT',
      },
      {
        emoji: '📝',
        text: 'Notion AI — Para organizar documentación',
        tag: 'TOOL_NOTION',
      },
      {
        emoji: '🔬',
        text: 'NotebookLM — Para analizar documentos propios',
        tag: 'TOOL_NOTEBOOK',
      },
      {
        emoji: '🗺️',
        text: 'Miro AI — Para mapas mentales y brainstorming',
        tag: 'TOOL_MIRO',
      },
      {
        emoji: '📋',
        text: 'JIRA — Para gestión de tareas y tickets',
        tag: 'TOOL_JIRA',
      },
      {
        emoji: '⚡',
        text: 'Zapier — Para automatizar flujos de trabajo',
        tag: 'TOOL_ZAPIER',
      },
      {
        emoji: '📊',
        text: 'Power BI — Para dashboards e indicadores',
        tag: 'TOOL_POWERBI',
      },
      { emoji: '🚫', text: 'Ninguna de las anteriores', tag: 'TOOL_NONE' },
    ],
  },
  {
    context: 'Imagina que pudieras automatizar UNA cosa mañana mismo...',
    question:
      'Si la IA pudiera automatizar UNA parte de tu trabajo hoy, ¿cuál elegirías?',
    type: 'single',
    reinforcement: {
      highOptions: [0, 1, 2, 3, 4, 5],
      high: '✨ Eso es exactamente lo que resuelve el sistema de IA para gestión de proyectos. Aparecerá en tu plan personalizado.',
      low: '✨ Eso es exactamente lo que resuelve el sistema de IA para gestión de proyectos.',
    },
    options: [
      {
        emoji: '📄',
        text: 'La generación de actas, reportes y documentación',
        tag: 'AUTO_DOC',
      },
      {
        emoji: '📊',
        text: 'La creación de dashboards y seguimiento de KPIs',
        tag: 'AUTO_KPI',
      },
      {
        emoji: '✅',
        text: 'La asignación y seguimiento de tareas del equipo',
        tag: 'AUTO_TASKS',
      },
      {
        emoji: '🧠',
        text: 'El análisis de riesgos y toma de decisiones',
        tag: 'AUTO_RISK',
      },
      {
        emoji: '📧',
        text: 'La comunicación con stakeholders y clientes',
        tag: 'AUTO_COMM',
      },
      {
        emoji: '🔄',
        text: 'Las retrospectivas y gestión del conocimiento',
        tag: 'AUTO_RETRO',
      },
    ],
  },
  {
    context: 'Esta pregunta es clave para tu resultado final...',
    question:
      '¿Conoces el Prompt Engineering y sabes cómo aplicarlo específicamente a la gestión de proyectos?',
    type: 'single',
    reinforcement: {
      highOptions: [1, 2, 3, 4],
      high: '🔑 El Prompt Engineering aplicado a proyectos (Método CERTO) es la habilidad #1 que diferencia a los PMs que aprovechan la IA. Aparecerá en tu plan.',
      low: '💪 Excelente. Hay una versión especializada del Prompt Engineering para PM que multiplica sus resultados por 5. Aparecerá en tu plan.',
    },
    options: [
      {
        emoji: '💡',
        text: 'Sí, lo domino y lo aplico regularmente',
        tag: 'PROMPT_EXPERT',
      },
      {
        emoji: '📚',
        text: 'Lo conozco superficialmente pero no sé aplicarlo en proyectos',
        tag: 'PROMPT_BASIC',
      },
      {
        emoji: '👂',
        text: 'He escuchado el término pero no entiendo bien qué es',
        tag: 'PROMPT_HEARD',
      },
      {
        emoji: '❓',
        text: 'No tenía idea de que existía el Prompt Engineering',
        tag: 'PROMPT_NONE',
      },
      {
        emoji: '🔍',
        text: 'Sé qué es pero no que se podía aplicar a proyectos',
        tag: 'PROMPT_CURIOUS',
      },
    ],
  },
  {
    context: 'Hablemos de tu futuro profesional...',
    question:
      '¿Qué tan importante es para ti obtener una certificación reconocida internacionalmente que valide tus habilidades en IA?',
    type: 'single',
    reinforcement: null,
    options: [
      {
        emoji: '🏆',
        text: 'Muy importante — Es algo que he buscado activamente',
        tag: 'CERT_HIGH',
      },
      {
        emoji: '⭐',
        text: 'Importante — Lo consideraría si la oportunidad es buena',
        tag: 'CERT_MED',
      },
      {
        emoji: '📈',
        text: 'Algo importante — Depende del reconocimiento real',
        tag: 'CERT_CONDITIONAL',
      },
      {
        emoji: '🌍',
        text: 'Crucial — Quiero que mi certificado sea válido en Europa y el mundo',
        tag: 'CERT_EUROPE',
      },
      {
        emoji: '🤔',
        text: 'No lo había considerado — Pero me parece interesante',
        tag: 'CERT_AWAKENED',
      },
    ],
  },
  {
    context: 'Visualiza esto por un momento...',
    question:
      'Si la IA automatizara el 60% de tus tareas repetitivas... ¿cuánto tiempo libre recuperarías a la semana?',
    type: 'single',
    reinforcement: {
      highOptions: [0, 1],
      high: '🚀 Imagina lo que podrías lograr invirtiendo ese tiempo en estrategia, liderazgo o simplemente descansando.',
      low: '🚀 Imagina lo que podrías lograr invirtiendo ese tiempo en estrategia, liderazgo o simplemente descansando.',
    },
    options: [
      { emoji: '⏳', text: 'Más de 10 horas a la semana', tag: 'TIME_10' },
      { emoji: '⌛', text: 'Entre 5 y 10 horas a la semana', tag: 'TIME_5_10' },
      { emoji: '⏱️', text: 'Entre 2 y 5 horas a la semana', tag: 'TIME_2_5' },
      { emoji: '⌚', text: 'Menos de 2 horas a la semana', tag: 'TIME_2' },
    ],
  },
  {
    context: 'Casi terminamos. Sobre tu desarrollo profesional...',
    question:
      '¿Estás dispuesto/a a invertir tiempo y recursos en actualizar tus habilidades para no quedarte atrás?',
    type: 'single',
    reinforcement: null,
    options: [
      {
        emoji: '💯',
        text: 'Totalmente — Es mi prioridad número uno',
        tag: 'INVEST_HIGH',
      },
      {
        emoji: '👍',
        text: 'Sí — Si veo un retorno claro de inversión',
        tag: 'INVEST_MED',
      },
      {
        emoji: '🤷',
        text: 'Tal vez — Depende del costo y tiempo',
        tag: 'INVEST_LOW',
      },
      {
        emoji: '❌',
        text: 'No por ahora — No tengo tiempo ni recursos',
        tag: 'INVEST_NONE',
      },
    ],
  },
  {
    context: 'Última pregunta...',
    question:
      '¿Qué tan pronto te gustaría empezar a implementar IA en tus proyectos y ver resultados?',
    type: 'single',
    reinforcement: {
      highOptions: [0, 1],
      high: '⚡ Perfecto. El plan que hemos preparado para ti está diseñado para dar resultados desde la primera semana.',
      low: '⚡ Perfecto. El plan que hemos preparado para ti está diseñado para dar resultados desde la primera semana.',
    },
    options: [
      {
        emoji: '🔥',
        text: 'De inmediato — Esta misma semana',
        tag: 'URGENCY_NOW',
      },
      {
        emoji: '📅',
        text: 'En los próximos 15 a 30 días',
        tag: 'URGENCY_SOON',
      },
      {
        emoji: '🗓️',
        text: 'En los próximos 3 a 6 meses',
        tag: 'URGENCY_LATER',
      },
      {
        emoji: '🐢',
        text: 'No tengo prisa — Solo estoy explorando',
        tag: 'URGENCY_NONE',
      },
    ],
  },
]
